import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../apiClient';
import PageLayout from '../../components/layout/PageLayout';
import ActivityMetrics from './components/ActivityMetrics';
import StageConversionFunnel from './components/StageConversionFunnel';
import KeyConversions from './components/KeyConversions';
import OutputMetrics from './components/OutputMetrics';
import './TADashboard.css';

function TADashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [recruiter, setRecruiter] = useState('');
  const [department, setDepartment] = useState('');
  const [filterOptions, setFilterOptions] = useState({ recruiters: [], departments: [] });

  // Set default date range to current month
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  }, []);

  // Fetch filter options
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const response = await apiClient.get('/ta-dashboard/filters');
        if (response.data.success) {
          setFilterOptions(response.data.data);
        }
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };
    fetchFilters();
  }, []);

  // Fetch dashboard metrics
  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (recruiter) params.append('recruiter', recruiter);
      if (department) params.append('department', department);

      // apiClient handles base URL and Authorization header automatically
      const response = await apiClient.get(`/ta-dashboard/metrics?${params.toString()}`);

      if (response.data.success) {
        setDashboardData(response.data.data);
      } else {
        throw new Error(response.data.error || 'Failed to load data');
      }
    } catch (err) {
      console.error('Error fetching dashboard metrics:', err);
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, recruiter, department]);

  // Fetch metrics when filters change
  useEffect(() => {
    if (startDate && endDate) {
      fetchMetrics();
    }
  }, [startDate, endDate, recruiter, department, fetchMetrics]);

  // Loading state
  if (loading && !dashboardData) { // Only show full page loading on initial load
    return (
      <div className="ta-dashboard-loading">
        <div className="ta-loading-spinner"></div>
        <div className="ta-loading-text">Loading dashboard metrics...</div>
      </div>
    );
  }

  // Error state
  if (error && !dashboardData) {
    return (
      <div className="ta-dashboard-error">
        <div className="ta-error-message">Error: {error}</div>
        <button className="ta-retry-button" onClick={fetchMetrics}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <PageLayout>
      <div className="ta-dashboard-container">
        <div className="ta-dashboard-header">
          <h1 className="ta-dashboard-title">TA Dashboard</h1>
          <div className="ta-dashboard-filters">
            <div className="ta-filter-group">
              <label className="ta-filter-label">Start Date</label>
              <input
                type="date"
                className="ta-filter-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="ta-filter-group">
              <label className="ta-filter-label">End Date</label>
              <input
                type="date"
                className="ta-filter-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="ta-filter-group">
              <label className="ta-filter-label">Recruiter</label>
              <select
                className="ta-filter-input"
                value={recruiter}
                onChange={(e) => setRecruiter(e.target.value)}
              >
                <option value="">All Recruiters</option>
                {filterOptions.recruiters.map((r, index) => (
                  <option key={index} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="ta-filter-group">
              <label className="ta-filter-label">Department</label>
              <select
                className="ta-filter-input"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
              >
                <option value="">All Departments</option>
                {filterOptions.departments.map((d, index) => (
                  <option key={index} value={d}>{d}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="ta-dashboard-content">
          {dashboardData && (
            <>
              <ActivityMetrics data={dashboardData.activity} />
              <StageConversionFunnel data={dashboardData.stageConversions} />
              <KeyConversions data={dashboardData.keyConversions} />
              <OutputMetrics data={dashboardData.output} />
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
}

export default TADashboard;